import type { Media } from '@wix/ricos/schema-next';
import type { DevContext } from '@wix/ricos/testkit';
import React from 'react';

import { MockModal } from './mock-modal';
import type { OpenModalFn } from './mock-modal-context';
import { testItems } from './pick-media-mocks';

type PickMediaFnResult = Awaited<
  ReturnType<Required<React.ContextType<typeof DevContext>>['pickMedia']>
>;

export const pickMediaModal =
  (openModal: OpenModalFn) =>
  ({
    mimeType,
    multiple,
  }: {
    mimeType: string;
    multiple: boolean;
  }): Promise<
    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    | { type: 'file'; file: File[]; customUploadFn?: Function }
    | { type: 'ready-media'; media: (Media | any)[] }
    | { type: 'cancel' }
  > => {
    const items = testItems.filter(item => {
      if (item.type === 'video' && mimeType.includes('video/')) {
        return true;
      }
      if (item.type === 'image' && mimeType.includes('image/')) {
        return true;
      }
      if (item.type === 'audio' && mimeType.includes('mp3')) {
        return true;
      }
      if (item.type === 'file' && mimeType === '*') {
        return true;
      }
      return false;
    });

    return new Promise<PickMediaFnResult>(resolve => {
      openModal(close => (
        <MockModal
          multiple={multiple}
          items={items.map(item => {
            switch (item.type) {
              case 'image':
                return {
                  label: `image ${item.width}x${item.height}`,
                  imageUrl: getPreviewUrl(item.id),
                };
              case 'video':
                return {
                  label: item.title,
                  imageUrl: getPreviewUrl(item.thumbnail.pathname.slice('media/'.length)),
                };
              case 'audio':
              case 'file':
                return {
                  label: item.name,
                };

              default:
                throw unreachable(item);
            }
          })}
          close={close}
          onConfirm={async ({ selected, mode }) => {
            const testItems = selected.map(i => items[i]);
            if (mode.type === 'manager') {
              const media = testItems.map(testItem => {
                switch (testItem.type) {
                  case 'video':
                    return {
                      pathname: testItem.id,
                      thumbnail: testItem.thumbnail,
                    };
                  case 'image':
                    return {
                      src: { id: testItem.id },
                      width: testItem.width,
                      height: testItem.height,
                      file_name: testItem.id,
                    };
                  case 'audio':
                    return {
                      audio: { src: { id: testItem.id } },
                      name: testItem.name,
                      authorName: testItem.authorName,
                    };
                  case 'file':
                    return {
                      name: testItem.name,
                      type: testItem.name.split('.').pop(),
                      url: testItem.url,
                      size: 150000,
                      sizeInKb: '150',
                    };
                  default:
                    throw unreachable(testItem);
                }
              });
              resolve({ type: 'ready-media', media });
              return;
            }

            const files = await Promise.all(
              testItems.map(async testItem => {
                switch (testItem.type) {
                  case 'video':
                    return { name: testItem.id, type: 'video/mp4' } as any;
                  case 'image':
                    return createFileFromImageUrl(getMediaUrl(testItem.id), testItem.id);
                  case 'audio':
                    return { name: testItem.id, type: 'audio/mp3' } as any;
                  case 'file':
                    return { name: testItem.name, type: testItem.type } as any;
                  default:
                    throw unreachable(testItem);
                }
              })
            );
            resolve({
              type: 'file',
              file: files.map(file => new File([file], file.name, { type: file.type })),
              customUploadFn: (file, cb) => {
                const name = file.name;
                const testItem = testItems.find(item => item.id === name);
                if (!testItem) {
                  throw Error('test item not found');
                }
                const callbackData = (() => {
                  switch (testItem.type) {
                    case 'video':
                      return {
                        pathname: testItem.id,
                        thumbnail: testItem.thumbnail,
                        title: testItem.id,
                      };
                    case 'image':
                      return {
                        id: testItem.id,
                        url: testItem.id,
                        ogiginal_file_name: testItem.id,
                        file_name: testItem.id,
                        height: testItem.height,
                        width: testItem.width,
                      };

                    case 'audio':
                      return {
                        name: testItem.name,
                        authorName: testItem.authorName,
                        audio: { src: { id: testItem.id } },
                        id: testItem.id,
                      };
                    case 'file':
                      return {
                        name: testItem.name,
                        type: testItem.name.split('.').pop(),
                        id: testItem.url,
                      };
                    default:
                      throw unreachable(testItem);
                  }
                })();

                sleep(mode.delay).then(() => {
                  mode.failWith !== undefined
                    ? cb({
                        error: {
                          key: mode.failWith,
                          // here we provide a mush of defaults for all possible errors
                          // @ts-expect-error
                          maxLimit: 100,
                          upgradeUrl: 'https://www.wix.com',
                          usage: 100,
                          limit: 100,
                          codec: 'xyz',
                          fps: 60,
                          videoDuration: 100,
                          audioDuration: 200,
                        },
                      })
                    : cb({ data: callbackData });
                });
              },
            });
          }}
        />
      ));
    });
  };

async function createFileFromImageUrl(imageUrl: string, fileName: string) {
  const response = await fetch(imageUrl);
  const blob = await response.blob();
  const file = new File([blob], fileName, {
    type: blob.type,
    lastModified: Date.now(),
  });
  // @ts-expect-error
  file.id = fileName;
  return file;
}

const getMediaUrl = (id: string) =>
  `https://static.wixstatic.com/media/${id}/v1/fit/w_500,h_500,al_c,q_80,usm_0.66_1.00_0.01,enc_auto/${id}`;

const getPreviewUrl = (id: string) =>
  `https://static.wixstatic.com/media/${id}/v1/fit/w_165,h_165,al_c,q_80,usm_0.66_1.00_0.01,enc_auto/${id}`;

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const unreachable = (x: never) => {
  return Error('unreachable');
};
